import { Component, inject, signal } from '@angular/core'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { ColDef, IHeaderParams } from 'ag-grid-community'
import { TableHeaderSortButtonComponent } from './table-header-sort-button/table-header-sort-button.component'
import { TableHeaderFilterButtonComponent } from './table-header-filter-button/table-header-filter-button.component'
import { SortEvent, TableSortService } from '../services/table-sort.service'
import { FlagFilterModalComponent } from '../../modals/filters/flag-filter-modal'
import { EntityFilterModalComponent } from '../../modals/filters/entity-filter-modal.component'
import { DateRangeFilterModalComponent } from '../../modals/filters/date-range-filter-modal.component'
import { ServicePointFilterModalComponent } from '../../modals/filters/service-point-filter-modal.component'
import { StringFilterModalComponent } from '../../modals/filters/string-filter-modal.component'
import { IHeaderAngularComp } from 'ag-grid-angular'

export type TableHeaderComponentModal =
  DateRangeFilterModalComponent |
  FlagFilterModalComponent |
  EntityFilterModalComponent |
  ServicePointFilterModalComponent |
  StringFilterModalComponent

export type TableHeaderComponentParams = Readonly<{
  filterGroup: string
  sortField?: string
  additionalFields: readonly string[]
  modal?: TableHeaderComponentModal
  modalOptions?: NgbModalOptions
  modalInit?: unknown
}>

const defaultTableHeaderComponentParams: TableHeaderComponentParams = {
  filterGroup: '',
  additionalFields: [],
}

@Component({
  imports: [TableHeaderSortButtonComponent, TableHeaderFilterButtonComponent],
  selector: 'app-table-header',
  styles: `
    :host {
      position: relative;
      display: grid;
      grid-template-columns: 1fr minmax(0, 1.5rem);
      gap: 0.25rem;
      justify-content: space-between;
      align-items: center;
      font-size: 1rem;
      font-weight: 700;
      width: 100%;
    }`,
  template: `
    <button
      app-table-header-sort-button
      [label]="headerName()"
      [field]="params().sortField ?? field()"
      [sortable]="sortable()"
      [currentSort]="sortService.getCurrentSort(params().sortField ?? field())"
      (sortChange)="handleSortChange($event)"
    ></button>

    @if(filterable()) {
    <button
      app-table-header-filter-button
      [filterGroup]="params().filterGroup"
      [fields]="params().additionalFields.concat(field())"
      (click)="onFilterClick()"
    ></button>
    }
  `,
})
export class TableHeaderComponent implements IHeaderAngularComp {
  private modalService = inject(NgbModal)
  protected sortService = inject(TableSortService)

  protected readonly params = signal<TableHeaderComponentParams>(defaultTableHeaderComponentParams)
  protected readonly field = signal<string>('')
  protected readonly headerName = signal<string>('')
  protected readonly sortable = signal<boolean>(false)
  protected readonly filterable = signal<boolean>(false)

  agInit(params: IHeaderParams): void {
    this.refresh(params)
  }

  refresh({ column }: IHeaderParams): boolean {
    this.params.set({ ...defaultTableHeaderComponentParams, ...column.getColDef().headerComponentParams })
    this.field.update(v => column.getColDef().field ?? v)
    this.headerName.update(v => column.getColDef().headerName ?? v)
    this.sortable.set(column.isSortable())
    this.filterable.set(column.isSortable())
    this.filterable.set(column.isFilterAllowed() && this.params().modal !== undefined)
    return true
  }

  protected handleSortChange(event: SortEvent) {
    this.sortService.next(event)
  }

  protected onFilterClick() {
    if (this.filterable()) {
      const { filterGroup, modal, modalOptions, modalInit } = this.params()
      const ref = this.modalService.open(modal, modalOptions)
      const component = ref.componentInstance as HeaderModal
      component.init(filterGroup, this.field(), modalInit)
      ref.result
        // eslint-disable-next-line no-console
        .then(() => console.debug(`[${this.field}] Modal close`))
        // eslint-disable-next-line no-console
        .catch(() => console.debug(`[${this.field}] Modal dismiss`))
    } else {
      // eslint-disable-next-line no-console
      console.debug(
        `[${this.field}] Filter could not be opened because the header is not filterable`,
      )
    }
  }
}

export interface HeaderModal<INIT = unknown> {
  init(filterGroup: string, field: string, modalInit: INIT): void;
}

export type HeaderProps<D, M> = {
  filterGroup: string,
  headerName?: string,
  field?: keyof D & string,
  filterable?: boolean,
  sortable?: boolean,
} & (M extends HeaderModal<infer I> 
  ? {
    modal: unknown,
    modalInit: I,
    modalOptions?: Record<string, unknown>,
  } 
  : Record<string, unknown>)

export const withHeader = <D, M= unknown>(props: HeaderProps<D, M>) => (def: ColDef): ColDef => ({
  ...def, 
  headerName: props.headerName,
  field: props.field,
  filter: props.filterable ?? false,
  sortable: props.sortable ?? false,
  headerComponent: TableHeaderComponent,
  headerComponentParams: {
    filterGroup: props.filterGroup,
    modal: props.modal,
    modalOptions: { centered: true, ...(props.modalOptions ?? {}) },
    modalInit: props.modalInit,
  },
})